import type { HTMLChakraProps, ResponsiveValue } from "@chakra-ui/react";
import { chakra, forwardRef } from "@chakra-ui/react";
import { __DEV__, cx } from "../../../utils/chakra";
import { isValidElement } from "react";

interface AspectRatioOptions {
    ratio?: ResponsiveValue<string>;
}

export interface AspectRatioProps extends HTMLChakraProps<"div">, AspectRatioOptions {
    children?: React.ReactNode;
}

/**
 * Chakra UI uses ye olde padding-bottom + pseudo trick to do aspect ratio - we can use the new pure CSS solution because of our target browsers
 */
export const AspectRatio = forwardRef<AspectRatioProps, "div">(
    ({ ratio = "4/3", children, className, ...props }, ref) => {
        if (!isValidElement(children)) {
            throw new Error("AspectRatio expects a single React element child.");
        }

        const _className = cx("chakra-aspect-ratio", className);

        return (
            <chakra.div
                ref={ref}
                {...props}
                className={_className}
                __css={{
                    position: "relative",
                    aspectRatio: ratio,
                    "& > *:not(style)": {
                        overflow: "hidden",
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                    },
                    "& > img, & > video": {
                        objectFit: "cover",
                    },
                }}
            >
                {children}
            </chakra.div>
        );
    }
);

if (__DEV__) {
    AspectRatio.displayName = "AspectRatio";
}
